<script setup lang="ts">
import { PhPulse } from "@phosphor-icons/vue"
import { computed, onMounted, ref } from "vue"
import ChartPanel from "../components/ChartPanel.vue"
import DataState from "../components/DataState.vue"
import DashboardCard from "../components/DashboardCard.vue"
import EventTimeline from "../components/EventTimeline.vue"
import MetricTile from "../components/MetricTile.vue"
import PageHeader from "../components/PageHeader.vue"
import StatusBadge from "../components/StatusBadge.vue"
import DashboardLayout from "../layouts/DashboardLayout.vue"
import { useDashboardStore } from "../stores/dashboard"
import { CurveType, DonutChart, DottedMap, LegendPosition, LineChart } from "../utils/nuxt-charts"
import type { ActiveConnection, RecentEvent } from "../utils/dashboard-types"
import { formatBytes, formatCompact, formatNumber, formatRelative, formatTimestamp } from "../utils/format"

type RegionCapacityRow = {
  activeClients: number
  constrainedSlots: number
  freeSlots: number
  medianLatencyMs: number | null
  nodes: number
  region: string
}

type ConnectionPressureRow = {
  active: number
  label: string
  paused: number
  warming: number
}

type TelemetryWindowRow = {
  error: number
  info: number
  label: string
  warn: number
}

type AttentionRow = {
  connections: number
  constrainedSlots: number
  freeSlots: number
  medianLatencyMs: number | null
  region: string
  score: number
}

type CapacityMapPoint = {
  label: string
  lat: number
  lng: number
}

type CapacityMapRow = RegionCapacityRow &
  CapacityMapPoint & {
    color: string
  }

type CapacityMapPin = {
  data: {
    activeClients: number
    constrainedSlots: number
    freeSlots: number
    label: string
    nodes: number
    region: string
  }
  lat: number
  lng: number
  svgOptions: {
    color: string
    radius: number
    strokeColor: string
    strokeOpacity: number
    strokeWidth: number
  }
}

const dashboard = useDashboardStore()
const chartsReady = ref(false)

const summary = computed(() => dashboard.summary)
const timelineEvents = computed(() =>
  (dashboard.events.length > 0
    ? [...dashboard.events].sort((left, right) => right.timestamp - left.timestamp)
    : summary.value.recentEvents
  ).slice(0, 12)
)
const now = computed(() => summary.value.generatedAt)

const availableNodes = computed(() => dashboard.nodes.filter((node) => node.status === "available").length)
const assignableNodes = computed(
  () =>
    dashboard.nodes.filter((node) => node.status === "available" && node.acceptingClients && node.charging !== false)
      .length
)
const androidNodes = computed(() => dashboard.nodes.filter((node) => node.nodeCategory === "android-sdk"))
const nodesById = computed(() => new Map(dashboard.nodes.map((node) => [node.id, node])))
const chargingAndroidNodes = computed(() => androidNodes.value.filter((node) => node.charging).length)
const activeConnectionCount = computed(
  () => dashboard.connections.filter((connection) => connection.status === "active").length
)
const pairedRelayCount = computed(() => dashboard.relayPairs.filter((pair) => pair.status === "paired").length)
const idleExpiringRelayCount = computed(
  () => dashboard.relayPairs.filter((pair) => pair.status === "idle-expiring").length
)
const unpairedRelayCount = computed(() => dashboard.relayPairs.filter((pair) => pair.status === "unpaired").length)
const relayPairHealth = computed(() => percent(pairedRelayCount.value, dashboard.relayPairs.length))
const eventErrorCount = computed(() => dashboard.events.filter((event) => event.level === "error").length)
const eventWarnCount = computed(() => dashboard.events.filter((event) => event.level === "warn").length)
const telemetryRiskCount = computed(
  () =>
    eventErrorCount.value +
    (dashboard.eventIngest.hardEvents ?? 0) +
    (dashboard.eventIngest.rejectedBatches ?? 0) +
    (dashboard.eventIngest.sanitizationDropCount ?? 0) +
    dashboard.relaySignals.missing.length
)
const totalCapacitySlots = computed(() =>
  dashboard.nodes.reduce((total, node) => total + Math.max(0, node.maxClients), 0)
)
const usedCapacitySlots = computed(() =>
  dashboard.nodes.reduce((total, node) => total + Math.max(0, node.activeClients ?? 0), 0)
)
const assignableSlots = computed(() =>
  dashboard.nodes.reduce((total, node) => {
    if (node.status !== "available" || !node.acceptingClients || node.charging === false) {
      return total
    }
    return total + Math.max(0, node.maxClients - (node.activeClients ?? 0))
  }, 0)
)
const capacityUtilization = computed(() => percent(usedCapacitySlots.value, totalCapacitySlots.value))
const activeNodeRatio = computed(() => percent(availableNodes.value, dashboard.nodes.length))
const chargingAndroidRatio = computed(() => percent(chargingAndroidNodes.value, androidNodes.value.length))
const credentialHorizonLabel = computed(() =>
  credentialHorizon(dashboard.credentialHealth.earliestExpiresAt, now.value)
)
const postureScore = computed(() => {
  const offlinePenalty = percent(
    dashboard.nodes.filter((node) => node.status === "offline" || node.status === "stale").length,
    dashboard.nodes.length
  )
  const relayPenalty = 100 - relayPairHealth.value
  const credentialPenalty = dashboard.credentialHealth.expiringWithin20Minutes > 0 ? 10 : 0
  const telemetryPenalty = Math.min(18, telemetryRiskCount.value * 3)
  return clamp(
    Math.round(100 - offlinePenalty * 0.35 - relayPenalty * 0.25 - credentialPenalty - telemetryPenalty),
    0,
    100
  )
})
const postureStatus = computed(() => {
  if (postureScore.value >= 88) {
    return "ready"
  }
  if (postureScore.value >= 72) {
    return "review"
  }
  return "action needed"
})
const postureToneClass = "text-(--dash-accent)"

const chartLegendStyle = { marginTop: "0.85rem" }
const chartPadding = { bottom: 42, left: 40, right: 14, top: 12 }
const compactChartPadding = { bottom: 42, left: 34, right: 10, top: 10 }
const connectionPressureCategories = {
  active: { color: "#8fa88e", name: "Active" },
  paused: { color: "#c5a66a", name: "Paused" },
  warming: { color: "#9ea8aa", name: "Warming" }
}
const telemetryCategories = {
  error: { color: "#b9786f", name: "Error" },
  info: { color: "#9ea8aa", name: "Info" },
  warn: { color: "#c5a66a", name: "Warn" }
}
const relayPairCategories = {
  idleExpiring: { color: "#c5a66a", name: "Idle expiring" },
  paired: { color: "#8fa88e", name: "Paired" },
  unpaired: { color: "#737a76", name: "Unpaired" }
}
const relayRegionCoordinates: Record<string, CapacityMapPoint> = {
  ams: { label: "Amsterdam", lat: 52.3105, lng: 4.7683 },
  atl: { label: "Atlanta", lat: 33.6407, lng: -84.4277 },
  bom: { label: "Mumbai", lat: 19.0896, lng: 72.8656 },
  cdg: { label: "Paris", lat: 49.0097, lng: 2.5479 },
  del: { label: "New Delhi", lat: 28.5562, lng: 77.1 },
  dfw: { label: "Dallas", lat: 32.8998, lng: -97.0403 },
  dxb: { label: "Dubai", lat: 25.2532, lng: 55.3657 },
  ewr: { label: "Newark", lat: 40.6895, lng: -74.1745 },
  fra: { label: "Frankfurt", lat: 50.0379, lng: 8.5622 },
  gru: { label: "Sao Paulo", lat: -23.4356, lng: -46.4731 },
  hkg: { label: "Hong Kong", lat: 22.308, lng: 113.9185 },
  iad: { label: "Ashburn", lat: 38.9531, lng: -77.4565 },
  icn: { label: "Seoul", lat: 37.4602, lng: 126.4407 },
  lax: { label: "Los Angeles", lat: 33.9416, lng: -118.4085 },
  lhr: { label: "London", lat: 51.47, lng: -0.4543 },
  mad: { label: "Madrid", lat: 40.4983, lng: -3.5676 },
  mia: { label: "Miami", lat: 25.7959, lng: -80.287 },
  mxp: { label: "Milan", lat: 45.63, lng: 8.7231 },
  nrt: { label: "Tokyo", lat: 35.773, lng: 140.3929 },
  ord: { label: "Chicago", lat: 41.9742, lng: -87.9073 },
  sin: { label: "Singapore", lat: 1.3644, lng: 103.9915 },
  sjc: { label: "San Jose", lat: 37.3639, lng: -121.9289 },
  syd: { label: "Sydney", lat: -33.9399, lng: 151.1753 },
  waw: { label: "Warsaw", lat: 52.1657, lng: 20.9671 },
  yyz: { label: "Toronto", lat: 43.6777, lng: -79.6248 }
}
const countryCoordinates: Record<string, CapacityMapPoint> = {
  AU: { label: "Australia", lat: -25.2744, lng: 133.7751 },
  BR: { label: "Brazil", lat: -14.235, lng: -51.9253 },
  CA: { label: "Canada", lat: 56.1304, lng: -106.3468 },
  DE: { label: "Germany", lat: 51.1657, lng: 10.4515 },
  GB: { label: "United Kingdom", lat: 55.3781, lng: -3.436 },
  JP: { label: "Japan", lat: 36.2048, lng: 138.2529 },
  SG: { label: "Singapore", lat: 1.3521, lng: 103.8198 },
  US: { label: "United States", lat: 39.8283, lng: -98.5795 }
}

const regionCapacityRows = computed<RegionCapacityRow[]>(() => {
  const rows = new Map<
    string,
    { active: number; constrained: number; free: number; latencies: number[]; nodes: number }
  >()

  for (const node of dashboard.nodes) {
    const region = node.relayRegion ?? node.country ?? "unknown"
    const row = rows.get(region) ?? { active: 0, constrained: 0, free: 0, latencies: [], nodes: 0 }
    const activeClients = Math.max(0, node.activeClients ?? 0)
    const remainingSlots = Math.max(0, node.maxClients - activeClients)

    row.active += activeClients
    row.nodes += 1
    if (node.latencyMs !== null) {
      row.latencies.push(node.latencyMs)
    }

    if (node.status === "available" && node.acceptingClients && node.charging !== false) {
      row.free += remainingSlots
    } else {
      row.constrained += remainingSlots
    }

    rows.set(region, row)
  }

  return Array.from(rows.entries())
    .map(([region, row]) => ({
      activeClients: row.active,
      constrainedSlots: row.constrained,
      freeSlots: row.free,
      medianLatencyMs: median(row.latencies),
      nodes: row.nodes,
      region
    }))
    .sort((left, right) => right.activeClients + right.freeSlots - (left.activeClients + left.freeSlots))
    .slice(0, 8)
})
const capacityMapRows = computed<CapacityMapRow[]>(() =>
  regionCapacityRows.value.flatMap((row) => {
    const point = capacityCoordinate(row.region)
    if (point === null) {
      return []
    }

    return [{ ...row, ...point, color: capacityMapColor(row) }]
  })
)
const capacityMapPins = computed<CapacityMapPin[]>(() =>
  capacityMapRows.value.map((row) => ({
    data: {
      activeClients: row.activeClients,
      constrainedSlots: row.constrainedSlots,
      freeSlots: row.freeSlots,
      label: row.label,
      nodes: row.nodes,
      region: row.region
    },
    lat: row.lat,
    lng: row.lng,
    svgOptions: {
      color: row.color,
      radius: capacityMapRadius(row),
      strokeColor: "#070707",
      strokeOpacity: 1,
      strokeWidth: 0.34
    }
  }))
)
const unmappedCapacityCount = computed(() => regionCapacityRows.value.length - capacityMapRows.value.length)
const attentionRows = computed<AttentionRow[]>(() => {
  const connectionCounts = new Map<string, number>()
  for (const connection of dashboard.connections) {
    const node = connection.nodeId ? nodesById.value.get(connection.nodeId) : undefined
    const region = connection.edgeRegion ?? node?.relayRegion ?? connection.country ?? "unknown"
    connectionCounts.set(region, (connectionCounts.get(region) ?? 0) + 1)
  }

  return regionCapacityRows.value
    .map((row) => ({
      connections: connectionCounts.get(row.region) ?? 0,
      constrainedSlots: row.constrainedSlots,
      freeSlots: row.freeSlots,
      medianLatencyMs: row.medianLatencyMs,
      region: row.region,
      score:
        row.constrainedSlots * 2 +
        (row.freeSlots === 0 ? 14 : 0) +
        (row.medianLatencyMs === null ? 0 : Math.max(0, row.medianLatencyMs - 70) / 5)
    }))
    .sort((left, right) => right.score - left.score)
    .slice(0, 5)
})
const connectionPressureRows = computed<ConnectionPressureRow[]>(() =>
  bucketConnections(dashboard.connections, now.value, 6, 60 * 60_000)
)
const telemetryWindowRows = computed<TelemetryWindowRow[]>(() =>
  bucketEvents(dashboard.events, now.value, 6, 10 * 60_000)
)
const relayPairDonutData = computed(() => [
  idleExpiringRelayCount.value,
  pairedRelayCount.value,
  unpairedRelayCount.value
])
const relayTrafficTotal = computed(() =>
  dashboard.relayPairs.reduce((total, pair) => total + (pair.receivedBytes ?? 0) + (pair.sentBytes ?? 0), 0)
)
const medianRelayLatency = computed(() =>
  median(dashboard.relayPairs.map((pair) => pair.latencyMs).filter((value): value is number => value !== null))
)
const activeNodeTrend = computed(() => `${activeNodeRatio.value}% ready`)
const relayPairTrend = computed(() => `${formatNumber(pairedRelayCount.value)} paired`)
const telemetryTrend = computed(() => (telemetryRiskCount.value > 0 ? `${eventWarnCount.value} warnings` : "no gaps"))
const connectionTrend = computed(() => `${formatCompact(activeConnectionCount.value)} active`)

onMounted(() => {
  chartsReady.value = import.meta.env.MODE !== "test" && typeof ResizeObserver !== "undefined"
})

function percent(value: number, total: number): number {
  return total <= 0 ? 0 : Math.round((value / total) * 100)
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function capacityCoordinate(region: string): CapacityMapPoint | null {
  const trimmed = region.trim()
  if (!trimmed || trimmed === "unknown") {
    return null
  }

  const relayPoint = relayRegionCoordinates[trimmed.toLowerCase()]
  if (relayPoint) {
    return { ...relayPoint, label: relayPoint.label }
  }

  const countryPoint = countryCoordinates[trimmed.toUpperCase()]
  return countryPoint ? { ...countryPoint, label: countryPoint.label } : null
}

function capacityMapColor(row: RegionCapacityRow): string {
  if (row.freeSlots <= 0) {
    return "#b9786f"
  }
  if (row.constrainedSlots > row.freeSlots) {
    return "#c5a66a"
  }
  return "#8fa88e"
}

function capacityMapRadius(row: RegionCapacityRow): number {
  const totalSlots = row.activeClients + row.constrainedSlots + row.freeSlots
  return clamp(0.58 + Math.sqrt(totalSlots) / 18, 0.74, 1.24)
}

function median(values: number[]): number | null {
  if (values.length === 0) {
    return null
  }
  const sorted = [...values].sort((left, right) => left - right)
  const midpoint = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) {
    return sorted[midpoint]
  }
  return Math.round((sorted[midpoint - 1] + sorted[midpoint]) / 2)
}

function credentialHorizon(expiresAt: number | null, generatedAt: number): string {
  if (expiresAt === null) {
    return "n/a"
  }
  const minutes = Math.max(0, Math.round((expiresAt - generatedAt) / 60_000))
  if (minutes < 60) {
    return `${minutes}m`
  }
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

function bucketConnections(
  connections: ActiveConnection[],
  generatedAt: number,
  bucketCount: number,
  bucketSizeMs: number
): ConnectionPressureRow[] {
  return Array.from({ length: bucketCount }, (_, index) => {
    const bucketStart = generatedAt - (bucketCount - index) * bucketSizeMs
    const bucketEnd = bucketStart + bucketSizeMs
    const label = index === bucketCount - 1 ? "now" : `-${bucketCount - index - 1}h`
    const inBucket = connections.filter((connection) => {
      const timestamp = connection.startedAt ?? connection.lastEventAt ?? generatedAt
      return timestamp >= bucketStart && timestamp < bucketEnd
    })

    return {
      active: inBucket.filter((connection) => connection.status === "active").length,
      label,
      paused: inBucket.filter((connection) => connection.status === "paused").length,
      warming: inBucket.filter((connection) => connection.status === "warming").length
    }
  })
}

function bucketEvents(
  events: RecentEvent[],
  generatedAt: number,
  bucketCount: number,
  bucketSizeMs: number
): TelemetryWindowRow[] {
  return Array.from({ length: bucketCount }, (_, index) => {
    const bucketStart = generatedAt - (bucketCount - index) * bucketSizeMs
    const bucketEnd = bucketStart + bucketSizeMs
    const label = index === bucketCount - 1 ? "now" : `-${(bucketCount - index - 1) * 10}m`
    const inBucket = events.filter((event) => event.timestamp >= bucketStart && event.timestamp < bucketEnd)

    return {
      error: inBucket.filter((event) => event.level === "error").length,
      info: inBucket.filter((event) => event.level === "info").length,
      label,
      warn: inBucket.filter((event) => event.level === "warn").length
    }
  })
}

function formatAxisNumber(tick: number | Date): string {
  return typeof tick === "number" ? formatCompact(tick) : ""
}

function formatConnectionTick(_tick: number | Date, index?: number): string {
  return connectionPressureRows.value[index ?? 0]?.label ?? ""
}

function formatTelemetryTick(_tick: number | Date, index?: number): string {
  return telemetryWindowRows.value[index ?? 0]?.label ?? ""
}
</script>

<template>
  <DashboardLayout>
    <div class="grid gap-5">
      <PageHeader
        title="Network overview"
        description="Capacity, demand, relay, credentials, and ingestion."
        details="Current assignable capacity, active demand, relay pairing, credential expiry, and event ingestion from the Worker snapshot."
      >
        <template #actions>
          <StatusBadge :status="dashboard.sourceLabel" />
          <span class="dashboard-mono text-sm text-(--dash-muted)">{{ formatTimestamp(summary.generatedAt) }}</span>
        </template>
      </PageHeader>

      <DataState
        :loading="dashboard.isLoading"
        :error="dashboard.errorMessage"
        :empty="dashboard.nodes.length === 0 && dashboard.connections.length === 0"
        empty-title="No network records"
        empty-description="The Worker did not return node, connection, or session records. Check diagnostics and API authentication."
      >
        <section class="dashboard-main-grid">
          <div class="grid min-w-0 gap-4">
            <section class="grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
              <MetricTile
                label="Active nodes"
                :value="formatNumber(availableNodes)"
                tone="success"
                :trend="activeNodeTrend"
                :progress="activeNodeRatio"
                :caption="`${formatNumber(assignableNodes)} nodes are accepting broker work right now.`"
              />
              <MetricTile
                label="Assignable slots"
                :value="formatNumber(assignableSlots)"
                tone="success"
                :trend="`${capacityUtilization}% used`"
                :progress="100 - capacityUtilization"
                caption="Free client slots after charging, liveness, and capacity gates."
              />
              <MetricTile
                label="Paired relay sessions"
                :value="`${relayPairHealth}%`"
                tone="success"
                :trend="relayPairTrend"
                :progress="relayPairHealth"
                caption="Browser and Android participants paired through the relay."
              />
              <MetricTile
                label="Event ingestion issues"
                :value="formatNumber(telemetryRiskCount)"
                tone="neutral"
                :trend="telemetryTrend"
                :progress="Math.max(0, 100 - telemetryRiskCount * 10)"
                caption="Errors, hard events, rejected batches, sanitization drops, and missing relay signals."
              />
            </section>

            <section class="grid gap-4 2xl:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
              <ChartPanel title="Regional capacity map" eyebrow="Relay regions with assignable slots">
                <div class="dashboard-world-map-layout">
                  <div class="dashboard-world-map-canvas">
                    <DottedMap
                      v-if="chartsReady && capacityMapPins.length"
                      class="dashboard-world-map"
                      region-name="world"
                      :pins="capacityMapPins"
                      :show-controls="false"
                      :show-legend="false"
                      :map-height="76"
                      :dot-size="0.26"
                      :stroke-width="0"
                      color="rgba(241, 243, 242, 0.12)"
                      background-color="transparent"
                    />
                    <div v-else class="dashboard-chart-fallback">
                      <div
                        v-for="row in regionCapacityRows"
                        :key="row.region"
                        class="dashboard-capacity-fallback-row"
                        :title="`${row.region}: ${row.freeSlots} assignable slots`"
                      >
                        <span class="dashboard-mono text-xs text-(--dash-dim)">{{ row.region }}</span>
                        <div class="dashboard-progress">
                          <span
                            :style="{
                              width: `${percent(row.freeSlots, row.freeSlots + row.constrainedSlots + row.activeClients)}%`
                            }"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="dashboard-world-map-list">
                    <div class="flex items-center justify-between gap-3">
                      <span class="dashboard-label">Mapped regions</span>
                      <span class="dashboard-mono text-xs text-(--dash-dim)">
                        {{ formatNumber(capacityMapRows.length) }} / {{ formatNumber(regionCapacityRows.length) }}
                      </span>
                    </div>

                    <div class="grid gap-2">
                      <article
                        v-for="row in capacityMapRows"
                        :key="row.region"
                        class="dashboard-world-map-row"
                        :title="`${row.label}: ${row.freeSlots} assignable, ${row.constrainedSlots} constrained`"
                      >
                        <span class="dashboard-world-map-dot" :style="{ backgroundColor: row.color }" />
                        <div class="min-w-0">
                          <div class="flex min-w-0 items-center justify-between gap-2">
                            <span class="dashboard-mono truncate text-xs text-(--dash-ink)">{{ row.region }}</span>
                            <span class="dashboard-mono text-[0.66rem] text-(--dash-dim)"
                              >{{ formatNumber(row.nodes) }} nodes</span
                            >
                          </div>
                          <p class="mt-1 truncate text-xs text-(--dash-dim)">{{ row.label }}</p>
                        </div>
                        <span class="dashboard-mono justify-self-end text-xs text-(--dash-success)">
                          {{ formatNumber(row.freeSlots) }}
                        </span>
                      </article>
                    </div>

                    <p v-if="unmappedCapacityCount > 0" class="text-xs leading-5 text-(--dash-dim)">
                      {{ formatNumber(unmappedCapacityCount) }} region rows need coordinates before plotting.
                    </p>
                  </div>
                </div>
              </ChartPanel>

              <DashboardCard title="Availability checks" eyebrow="Inputs that affect assignment" static>
                <div class="grid gap-5">
                  <div class="flex items-end justify-between gap-4">
                    <div>
                      <p class="dashboard-mono text-6xl font-medium leading-none" :class="postureToneClass">
                        {{ postureScore }}
                      </p>
                      <p class="mt-2 text-sm text-(--dash-muted)">assignment readiness</p>
                    </div>
                    <StatusBadge :status="postureStatus" />
                  </div>

                  <div class="grid gap-3 text-sm">
                    <div class="dashboard-ops-row">
                      <span>Android charging</span>
                      <span class="dashboard-mono text-(--dash-ink)">{{ chargingAndroidRatio }}%</span>
                    </div>
                    <div class="dashboard-ops-row">
                      <span>Next credential expiry</span>
                      <span class="dashboard-mono text-(--dash-ink)">
                        {{ credentialHorizonLabel }}
                      </span>
                    </div>
                    <div class="dashboard-ops-row">
                      <span>Relay traffic</span>
                      <span class="dashboard-mono text-(--dash-ink)">{{ formatBytes(relayTrafficTotal) }}</span>
                    </div>
                    <div class="dashboard-ops-row">
                      <span>Median relay RTT</span>
                      <span class="dashboard-mono text-(--dash-ink)">
                        {{ medianRelayLatency === null ? "n/a" : `${medianRelayLatency} ms` }}
                      </span>
                    </div>
                  </div>
                </div>
              </DashboardCard>
            </section>

            <section class="grid gap-4 2xl:grid-cols-2">
              <ChartPanel title="Connections by state" eyebrow="Session starts in the last six hours">
                <div class="dashboard-chart-frame min-h-[286px]">
                  <LineChart
                    v-if="chartsReady"
                    class="dashboard-chart"
                    :data="connectionPressureRows"
                    :categories="connectionPressureCategories"
                    :height="248"
                    :padding="chartPadding"
                    :xFormatter="formatConnectionTick"
                    :yFormatter="formatAxisNumber"
                    :curveType="CurveType.MonotoneX"
                    :legendStyle="chartLegendStyle"
                    :legendPosition="LegendPosition.BottomCenter"
                    :lineWidth="1.35"
                    :xNumTicks="4"
                    :yNumTicks="4"
                    :duration="560"
                    xLabel="Start window"
                    yLabel="Connections"
                  />
                  <div v-else class="dashboard-chart-fallback dashboard-chart-fallback--center">
                    {{ connectionTrend }} connections in the latest window.
                  </div>
                </div>
              </ChartPanel>

              <ChartPanel title="Relay pair states" eyebrow="Participant state and relay counters">
                <div class="grid min-h-[286px] gap-4 md:grid-cols-[minmax(0,0.9fr)_minmax(210px,1.1fr)]">
                  <div class="dashboard-chart-frame dashboard-chart-frame--compact">
                    <DonutChart
                      v-if="chartsReady && dashboard.relayPairs.length"
                      class="dashboard-chart dashboard-chart--donut"
                      :data="relayPairDonutData"
                      :categories="relayPairCategories"
                      :height="232"
                      :radius="88"
                      :arcWidth="9"
                      :padAngle="0.026"
                      :hide-legend="true"
                      :duration="520"
                    />
                    <div v-else class="dashboard-chart-fallback dashboard-chart-fallback--center">No relay pairs.</div>
                  </div>

                  <div class="grid content-center gap-3 text-sm">
                    <div class="dashboard-ops-row">
                      <span>Paired sessions</span>
                      <span class="dashboard-mono text-(--dash-success)">{{ formatNumber(pairedRelayCount) }}</span>
                    </div>
                    <div class="dashboard-ops-row">
                      <span>Idle-expiring</span>
                      <span class="dashboard-mono text-(--dash-warning)">{{
                        formatNumber(idleExpiringRelayCount)
                      }}</span>
                    </div>
                    <div class="dashboard-ops-row">
                      <span>Unpaired</span>
                      <span class="dashboard-mono text-(--dash-muted)">{{ formatNumber(unpairedRelayCount) }}</span>
                    </div>
                    <div class="dashboard-ops-row">
                      <span>Endpoints</span>
                      <span class="dashboard-mono text-(--dash-ink)">{{
                        formatNumber(dashboard.relayEndpoints.length)
                      }}</span>
                    </div>
                  </div>
                </div>
              </ChartPanel>
            </section>

            <section class="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.72fr)]">
              <ChartPanel title="Events by severity" eyebrow="Accepted events in the last hour">
                <div class="dashboard-chart-frame min-h-[308px]">
                  <LineChart
                    v-if="chartsReady"
                    class="dashboard-chart"
                    :data="telemetryWindowRows"
                    :categories="telemetryCategories"
                    :height="246"
                    :padding="compactChartPadding"
                    :xFormatter="formatTelemetryTick"
                    :yFormatter="formatAxisNumber"
                    :curveType="CurveType.MonotoneX"
                    :legendStyle="chartLegendStyle"
                    :legendPosition="LegendPosition.BottomCenter"
                    :lineWidth="1.35"
                    :xNumTicks="4"
                    :yNumTicks="4"
                    :duration="560"
                    xLabel="Event window"
                    yLabel="Events"
                  />
                  <div v-else class="dashboard-chart-fallback dashboard-chart-fallback--center">
                    {{ formatNumber(dashboard.events.length) }} retained events.
                  </div>
                </div>
              </ChartPanel>

              <DashboardCard title="Regions with constraints" eyebrow="Capacity and relay latency" static>
                <div class="grid gap-3">
                  <article v-for="row in attentionRows" :key="row.region" class="dashboard-attention-row">
                    <div class="flex min-w-0 items-center justify-between gap-3">
                      <span class="dashboard-mono truncate text-sm text-(--dash-ink)">{{ row.region }}</span>
                      <span class="dashboard-mono text-xs text-(--dash-dim)"
                        >{{ formatNumber(row.connections) }} conn</span
                      >
                    </div>
                    <div class="mt-3 grid grid-cols-3 gap-2 text-xs text-(--dash-muted)">
                      <span
                        >free
                        <b class="dashboard-mono text-(--dash-success)">{{ formatNumber(row.freeSlots) }}</b></span
                      >
                      <span
                        >held
                        <b class="dashboard-mono text-(--dash-warning)">{{
                          formatNumber(row.constrainedSlots)
                        }}</b></span
                      >
                      <span>
                        rtt
                        <b class="dashboard-mono text-(--dash-ink)">
                          {{ row.medianLatencyMs === null ? "n/a" : `${row.medianLatencyMs}ms` }}
                        </b>
                      </span>
                    </div>
                  </article>
                </div>
              </DashboardCard>
            </section>
          </div>

          <aside class="dashboard-events-rail">
            <DashboardCard class="dashboard-events-card" title="Recent events" eyebrow="Newest accepted events" static>
              <template #actions>
                <PhPulse :size="18" class="text-(--dash-success)" />
              </template>
              <div class="dashboard-events-body">
                <EventTimeline :events="timelineEvents" :now="summary.generatedAt" />
              </div>
              <template #footer>
                <p class="text-xs text-(--dash-dim)">Updated {{ formatRelative(summary.generatedAt, Date.now()) }}</p>
              </template>
            </DashboardCard>
          </aside>
        </section>
      </DataState>
    </div>
  </DashboardLayout>
</template>
